import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { trackLocaleChange } from '@/services/analytics';

type Language = 'en' | 'de' | 'it' | 'fr';

interface LanguageContextType {
  currentLanguage: Language;
  changeLanguage: (language: Language) => void;
  availableLanguages: { code: Language; name: string }[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState<Language>('de');

  const availableLanguages = useMemo(() => [
    { code: 'de' as Language, name: 'Deutsch' },
    { code: 'en' as Language, name: 'English' },
    { code: 'it' as Language, name: 'Italiano' },
    { code: 'fr' as Language, name: 'Français' },
  ], []);

  // Extract language from URL path
  const getLanguageFromPath = useCallback((pathname: string): Language => {
    const pathSegments = pathname.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];

    if (firstSegment && availableLanguages.some(lang => lang.code === firstSegment)) {
      return firstSegment as Language;
    }

    // Default to German for root path
    return 'de';
  }, [availableLanguages]);

  const changeLanguage = (language: Language) => {
    const originalLocale = currentLanguage;
    const targetLocale = language;



    const currentPath = location.pathname;
    const pathSegments = currentPath.split('/').filter(Boolean);

    // Remove current language from path if it exists
    if (pathSegments.length > 0 && availableLanguages.some(lang => lang.code === pathSegments[0])) {
      pathSegments.shift();
    }

    // Build new path
    let newPath = '';
    if (language !== 'de') {
      newPath = `/${language}`;
    }
    if (pathSegments.length > 0) {
      newPath += `/${pathSegments.join('/')}`;
    }

    // Preserve query parameters and hash
    const queryString = location.search;
    const hash = location.hash;
    navigate(newPath + queryString + hash);

    i18n.changeLanguage(language);
    setCurrentLanguage(language);
    localStorage.setItem('i18nextLng', language);

    trackLocaleChange(originalLocale, targetLocale);
  };

  useEffect(() => {
    const languageFromPath = getLanguageFromPath(location.pathname);
    setCurrentLanguage(languageFromPath);
    i18n.changeLanguage(languageFromPath);
    localStorage.setItem('i18nextLng', languageFromPath);
  }, [location.pathname, i18n, getLanguageFromPath]);

  const value: LanguageContextType = {
    currentLanguage,
    changeLanguage,
    availableLanguages,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
